<template>
  <aside
    class="app-sidebar"
    :class="[
      minimized ? 'app-sidebar--minimized' : 'app-sidebar--expanded',
      isDarkTheme ? 'app-sidebar--dark' : 'app-sidebar--light',
    ]"
    aria-label="Navegação principal"
  >
    <!-- ── Conteúdo rolável ── -->
    <div class="sidebar-scroll">
      <!-- Busca (apenas expandida) -->
      <div v-if="!minimized" class="sidebar-search">
        <span class="material-icons sidebar-search__icon">search</span>
        <input
          v-model="searchQuery"
          class="sidebar-search__input"
          type="search"
          placeholder="Pesquisar..."
          aria-label="Pesquisar menu"
        />
      </div>

      <!-- Seções de navegação (sem Preferências) -->
      <nav class="sidebar-nav">
        <template v-for="section in visibleSections" :key="section.key">
          <div class="sidebar-section">
            <span v-if="!minimized" class="sidebar-section__label">{{ section.label }}</span>

            <div class="sidebar-items">
              <template v-for="item in section.items" :key="item.name">
                <!-- Item com filhos (accordion) -->
                <template v-if="item.children">
                  <button
                    class="sidebar-item"
                    :class="{ 'sidebar-item--active': isChildActive(item) }"
                    :title="minimized ? t(item.displayName) : undefined"
                    :aria-expanded="!minimized ? isExpanded(item.name) : undefined"
                    @click="minimized ? navigateToFirstChild(item) : toggleExpand(item.name)"
                  >
                    <span class="material-icons sidebar-item__icon">{{ item.meta.icon }}</span>
                    <template v-if="!minimized">
                      <span class="sidebar-item__label">{{ t(item.displayName) }}</span>
                      <span class="material-icons sidebar-item__chevron">
                        {{ isExpanded(item.name) ? 'expand_less' : 'expand_more' }}
                      </span>
                    </template>
                    <span v-if="minimized && isChildActive(item)" class="sidebar-item__active-dot" />
                  </button>

                  <div v-if="!minimized && isExpanded(item.name)" class="sidebar-children">
                    <RouterLink
                      v-for="child in item.children"
                      :key="child.name"
                      :to="{ name: child.name }"
                      class="sidebar-item sidebar-item--child"
                      :class="{ 'sidebar-item--active': isRouteActive(child.name) }"
                      role="link"
                      :aria-current="isRouteActive(child.name) ? 'page' : undefined"
                    >
                      <span class="sidebar-item__child-dot" aria-hidden="true" />
                      <span class="sidebar-item__label">{{ t(child.displayName) }}</span>
                    </RouterLink>
                  </div>
                </template>

                <!-- Item simples -->
                <RouterLink
                  v-else
                  :to="{ name: item.name }"
                  class="sidebar-item"
                  :class="{ 'sidebar-item--active': isRouteActive(item.name) }"
                  role="link"
                  :aria-current="isRouteActive(item.name) ? 'page' : undefined"
                  :title="minimized ? t(item.displayName) : undefined"
                >
                  <span class="material-icons sidebar-item__icon">{{ item.meta.icon }}</span>
                  <template v-if="!minimized">
                    <span class="sidebar-item__label">{{ t(item.displayName) }}</span>
                    <span v-if="counters[item.name]" class="sidebar-item__counter">
                      {{ counters[item.name] }}
                    </span>
                  </template>
                  <span v-if="minimized && counters[item.name]" class="sidebar-item__counter-dot" />
                </RouterLink>
              </template>
            </div>
          </div>
        </template>
      </nav>
    </div>

    <!-- ── Rodapé fixo: Preferências ── -->
    <div class="sidebar-footer">
      <div class="sidebar-footer__divider" />
      <RouterLink
        to="/preferences"
        class="sidebar-item sidebar-item--footer"
        :class="{ 'sidebar-item--active': isRouteActive('preferences') }"
        :title="minimized ? t('menu.preferences') : undefined"
        role="link"
        :aria-current="isRouteActive('preferences') ? 'page' : undefined"
      >
        <span class="material-icons sidebar-item__icon">manage_accounts</span>
        <span v-if="!minimized" class="sidebar-item__label">{{ t('menu.preferences') }}</span>
      </RouterLink>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useEffectiveWorkspace } from '@/composables/useEffectiveWorkspace'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user-store'
import NavigationRoutes, { INavigationRoute } from './NavigationRoutes'

const props = defineProps<{ minimized: boolean }>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const currentWorkspace = useEffectiveWorkspace()

// ── Theme sync ──────────────────────────────────────────────
const currentTheme = ref('light')
function syncTheme() {
  try {
    if (typeof window !== 'undefined' && (window as any).getAppTheme) {
      currentTheme.value = (window as any).getAppTheme() ?? 'light'
    }
  } catch (e) {}
}
const isDarkTheme = computed(() => currentTheme.value === 'dark')

onMounted(() => {
  syncTheme()
  window.addEventListener('app-theme-changed', syncTheme)
})
onBeforeUnmount(() => window.removeEventListener('app-theme-changed', syncTheme))

// ── Busca ───────────────────────────────────────────────────
const searchQuery = ref('')

// ── Accordion state ─────────────────────────────────────────
const expandedItems = ref<Set<string>>(new Set())

function isExpanded(name: string): boolean {
  return expandedItems.value.has(name)
}

function toggleExpand(name: string) {
  if (expandedItems.value.has(name)) {
    expandedItems.value.delete(name)
  } else {
    expandedItems.value.add(name)
  }
}

function navigateToFirstChild(item: INavigationRoute) {
  if (item.children?.length) {
    router.push({ name: item.children[0].name })
  }
}

// Auto-expande pai quando filho está ativo
watch(
  () => route.name,
  () => {
    for (const item of NavigationRoutes.routes) {
      if (item.children?.some((c) => c.name === route.name)) {
        expandedItems.value.add(item.name)
      }
    }
  },
  { immediate: true },
)

// ── Rota ativa ──────────────────────────────────────────────
function isRouteActive(name: string): boolean {
  return route.name === name
}

function isChildActive(item: INavigationRoute): boolean {
  return !!item.children?.some((c) => c.name === route.name)
}

// ── Counters (TODO: GET /api/menu/counters) ─────────────────
const counters = ref<Record<string, number | null>>({
  deployments: null,
  companies: null,
})

// ── Filtrar itens (excluindo Preferências — vai no rodapé) ───
const filteredRoutes = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()

  return NavigationRoutes.routes
    .filter((item) => item.name !== 'preferences') // Preferências fica no rodapé
    .filter((item) => (item.workspace ?? 'crm') === currentWorkspace.value)
    .map((item) => ({ ...item, children: item.children ? [...item.children] : undefined }))
    .filter((item) => {
      if (item.requiresAdmin && !userStore.isAdmin) return false
      if (item.requiresAiAgents && !userStore.aiAgentsAtLeast(item.requiresAiAgents)) return false
      if (!userStore.hasSupportAccess(item.requiresSupport)) return false

      if (!query) {
        if (item.children) {
          item.children = item.children.filter((c) => !(c as any).requiresAdmin || userStore.isAdmin)
        }
        return true
      }

      const label = t(item.displayName).toLowerCase()
      const match = label.includes(query)

      if (item.children) {
        const matchingChildren = item.children.filter((c) => {
          if ((c as any).requiresAdmin && !userStore.isAdmin) return false
          return t(c.displayName).toLowerCase().includes(query)
        })
        if (match || matchingChildren.length > 0) {
          if (!match) item.children = matchingChildren
          return true
        }
        return false
      }

      return match
    })
})

// ── Agrupar por seção ────────────────────────────────────────
const SECTION_LABELS: Record<string, string> = {
  service_desk: 'Service Desk',
  project: 'Project',
  operacao: 'Operação',
  cadastros: 'Cadastros',
  sistema: 'Sistema',
  ai_agents: 'AI Agents',
}

const visibleSections = computed(() => {
  const sections: Array<{ key: string; label: string; items: INavigationRoute[] }> = []
  const order = ['service_desk', 'project', 'operacao', 'ai_agents', 'cadastros', 'sistema']

  for (const key of order) {
    const items = filteredRoutes.value.filter((r) => (r.section ?? 'cadastros') === key)
    if (items.length > 0) {
      sections.push({ key, label: SECTION_LABELS[key], items })
    }
  }
  return sections
})
</script>

<style lang="scss">
// ── Sidebar container ────────────────────────────────────────
.app-sidebar {
  flex-shrink: 0;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--shell-glass-border);
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @supports (backdrop-filter: blur()) {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  @supports not (backdrop-filter: blur()) {
    &--dark {
      background: #1c2030 !important;
    }
    &--light {
      background: #fff !important;
    }
  }

  &--expanded {
    width: 240px;
  }
  &--minimized {
    width: 60px;
  }

  &--dark {
    background: var(--shell-glass-bg);
  }
  &--light {
    background: var(--shell-glass-bg);
  }
}

// ── Área rolável ─────────────────────────────────────────────
.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--shell-scrollbar);
    border-radius: 2px;
  }
}

// ── Campo de busca ───────────────────────────────────────────
.sidebar-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 9px;
  background: var(--shell-input-bg, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--shell-input-border, rgba(0, 0, 0, 0.08));
  margin-bottom: 8px;
  flex-shrink: 0;

  &__icon {
    font-size: 16px;
    color: var(--shell-text-muted);
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-size: 13px;
    color: var(--shell-text);
    font-family: inherit;
    min-width: 0;

    &::placeholder {
      color: var(--shell-text-muted);
    }
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
}

// ── Seções ───────────────────────────────────────────────────
.sidebar-section {
  margin-bottom: 6px;

  &__label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--shell-section-label);
    padding: 4px 8px 6px;
    font-family: monospace;
    user-select: none;
  }
}

.sidebar-items {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

// ── Item de navegação ─────────────────────────────────────────
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 11px;
  border-radius: 10px;
  text-decoration: none;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 13.5px;
  color: var(--shell-text);
  transition:
    background 0.15s,
    color 0.15s;
  position: relative;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;

  &:hover:not(.sidebar-item--active) {
    background: var(--shell-item-hover);
  }

  &:focus-visible {
    outline: 2px solid #ff4d2e;
    outline-offset: 2px;
  }

  &--active {
    background: #ff4d2e !important;
    color: #fff !important;
    box-shadow: 0 4px 14px -4px rgba(255, 77, 46, 0.5);

    .sidebar-item__icon {
      color: #fff !important;
    }
    .sidebar-item__counter {
      background: rgba(255, 255, 255, 0.25);
      color: #fff;
    }
  }

  &--child {
    padding-left: 14px;
    font-size: 13px;

    &.sidebar-item--active {
      background: rgba(255, 77, 46, 0.15) !important;
      color: #ff4d2e !important;
      box-shadow: none;

      .sidebar-item__child-dot {
        background: #ff4d2e;
      }
    }
  }

  &--footer {
    // item de preferências no rodapé
  }

  &__icon {
    font-size: 18px;
    flex-shrink: 0;
    color: var(--shell-text-muted);
    transition: color 0.15s;
  }

  &__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__counter {
    font-size: 11px;
    font-weight: 600;
    background: var(--shell-item-hover);
    color: var(--shell-text-muted);
    padding: 1px 6px;
    border-radius: 10px;
    flex-shrink: 0;
    min-width: 20px;
    text-align: center;
  }

  &__chevron {
    font-size: 16px;
    color: var(--shell-text-muted);
    flex-shrink: 0;
  }

  &__child-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--shell-text-muted);
    flex-shrink: 0;
    margin-left: 3px;
  }

  &__active-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff4d2e;
  }

  &__counter-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff4d2e;
  }
}

// ── Sub-itens accordion ───────────────────────────────────────
.sidebar-children {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-left: 4px;
  padding-left: 6px;
  border-left: 2px solid var(--shell-glass-border);
}

// ── Rodapé fixo (Preferências) ────────────────────────────────
.sidebar-footer {
  flex-shrink: 0;
  padding: 0 8px 10px;

  &__divider {
    height: 1px;
    background: var(--shell-glass-border);
    margin-bottom: 8px;
  }
}
</style>
